<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import useVuelidate from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useOtherEntityStore } from '../stores/other-entity-store'
import { capitalize, startCase as _startCase } from 'lodash'
import {
  VButton,
  VIcon,
  VLink,
  VRadio,
  VSection,
  VTaxIdField,
  VTextField,
  VDropdownMultiple,
} from '@/modules/shared/components'
import VSelect from './v-select.vue'
import { useInvestingInvestorStore } from '../stores/investor-store'
import DisbursementPreferencesForm from './disbursement-preferences-form.vue'
import { currencyOptions } from '@/modules/shared/utils/money'
import { useAddressStore } from '@/modules/shared/stores/address-store'

function startCase(str: string) {
  return str
    .split(' / ')
    .map((part) => _startCase(part))
    .join(' / ')
}

const NON_US_INVESTOR_TAX_ID_VALUE = 'NON-US INVESTOR'

const { t } = useExtendedI18n()
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  other_entity?: any
}>()

const otherEntityStore = useOtherEntityStore()
const addressStore = useAddressStore()
const investorStore = useInvestingInvestorStore()
const is_non_us_investor = ref(false)

const investorOptions = computed(() => {
  return investorStore.individuals
    .map((investor) => ({
      label: investor.name,
      value: investor.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const investorSetTypesOptions = computed(() => {
  const types = [
    'LLC',
    'LP',
    'LLP',
    'Ltd',
    'Non-Profit Corporation',
    'C-Corp',
    'S-Corp',
    'Pte Ltd',
    'Pty Ltd',
    'Trust',
    'IRA',
    'ROTH IRA',
    'Disregarded Entity',
    'Other',
  ]
  return types.map((type) => ({
    label: type,
    value: type,
  }))
})

const useManagementFee = ref(true)
const useCarriedInterest = ref(true)

const initialState = {
  id: null,
  name: '',
  date: null,
  type: '',
  currency: 'USD',
  tax_id: '',
  input_capital: false,
  management_fee_percentage: null,
  carried_interest: null,
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  disbursement_method: 'wire',
  disbursement_wire_bank_name: '',
  disbursement_wire_bank_address: '',
  disbursement_wire_bank_routing_number: '',
  disbursement_wire_bank_account_number: '',
  disbursement_wire_repeat_bank_account_number: '',
  disbursement_wire_bank_swift_code: '',
  disbursement_wire_bank_notes: '',
  disbursement_wire_account_name: '',
  disbursement_wire_for_further_credit_to: '',
  disbursement_check_receiver_name: '',
  disbursement_other_details: '',
  partner_name: '',
  partner_tax_id: '',
  investor_admin_ids: [],
}

const original_bank_account_number = ref('')
const check_bank_account_number = computed(
  () => original_bank_account_number.value !== otherEntity.value.disbursement_wire_bank_account_number,
)

const rules = {
  name: { required },
  disbursement_wire_repeat_bank_account_number: {
    sameAsRepeat: sameAs(
      computed(() =>
        check_bank_account_number.value
          ? otherEntity.value.disbursement_wire_bank_account_number
          : otherEntity.value.disbursement_wire_repeat_bank_account_number,
      ),
    ),
  },
}

const setOtherEntity = () => {
  if (props.other_entity) {
    original_bank_account_number.value = props.other_entity.disbursement_wire_bank_account_number
    return {
      ...Object.keys(initialState).reduce((data, key) => {
        if (key === 'investor_admin_ids') return data
        data[key] = props.other_entity[key]
        return data
      }, {}),
      investor_admin_ids: props.other_entity.admins.map((admin) => admin.id),
    }
  } else {
    return { ...initialState }
  }
}

const otherEntity = ref(setOtherEntity())
const v$ = useVuelidate(rules, otherEntity, { $lazy: true })

const saveOtherEntity = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return
  const payload = {
    funding_entity: {
      ...Object.keys(otherEntity.value).reduce((data, key) => {
        if (['investor_admin_ids', 'disbursement_wire_repeat_bank_account_number'].includes(key)) return data
        if (key === 'currency') {
          data['total_investment_currency'] = otherEntity.value[key]
          return data
        }
        data[key] = otherEntity.value[key]
        return data
      }, {}),
    },
    investor_admin_ids: otherEntity.value.investor_admin_ids,
  }

  if (props.other_entity) {
    await otherEntityStore.updateOtherEntity(payload)
    router.push({ name: 'investing.other-entity.overview' })
  } else {
    const data = await otherEntityStore.addOtherEntity(payload)
    if (data) router.push({ name: 'investing.other-entity.investors', params: { other_entity_id: data.id } })
  }
}

const removeOtherEntity = async () => {
  if (!window.confirm('Are you sure?')) return
  await otherEntityStore.removeOtherEntity(props.other_entity.id)
  router.push({ name: 'investing.other-entities' })
}

const toggleIsNonUsInvestor = () => {
  is_non_us_investor.value = !is_non_us_investor.value
}

watch(is_non_us_investor, () => {
  if (is_non_us_investor.value) {
    otherEntity.value.tax_id = NON_US_INVESTOR_TAX_ID_VALUE
  } else {
    otherEntity.value.tax_id = ''
  }
})

watch(
  () => otherEntity.value.country,
  async (countryCode, prevCountry) => {
    if (!countryCode) return
    await addressStore.fetchStates(countryCode)

    // Only reset when the user switches countries after load
    if (prevCountry && countryCode !== prevCountry) {
      otherEntity.value.state = null
    }
  },
)

onMounted(async () => {
  const element = document.getElementById(route.hash.replace('#', ''))
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  is_non_us_investor.value = otherEntity.value.tax_id === NON_US_INVESTOR_TAX_ID_VALUE

  await Promise.all([
    investorStore.fetchIndividuals('all'),
    addressStore.fetchCountries(),
    addressStore.fetchStates(props.other_entity?.country || 'US'),
  ])
})
</script>

<template>
  <VSection class="flex gap-3" id="general-information">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
        {{ capitalize(t('shared.general information')) }}
        <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">1/5</span>
      </h3>
    </div>
    <div class="w-1/2">
      <VTextField
        v-model="otherEntity.name"
        :label="capitalize(t('shared.name'))"
        property="name"
        :v$="v$"
        class="mb-5"
      />
      <div class="mb-5 flex gap-5">
        <VTextField
          v-model="otherEntity.date"
          :label="capitalize(t('shared.date established'))"
          property="date"
          type="date"
          :v$="v$"
          class="w-1/2"
        />
        <div class="w-1/2">
          <VSelect
            v-model="otherEntity.type"
            :disabled="false"
            :label="capitalize(t('shared.type'))"
            :options="investorSetTypesOptions"
            property="type"
            :v$="v$"
          />
        </div>
      </div>
      <VSelect
        v-model="otherEntity.currency"
        label="Currency"
        :options="currencyOptions"
        property="currency"
        :v$="v$"
        class="mb-5"
      />
      <template v-if="otherEntity.type === 'Disregarded Entity'">
        <VTextField
          v-model="otherEntity.partner_name"
          :label="`Partner name`"
          property="partner_name"
          :v$="v$"
          class="mb-5"
        />
        <VTextField
          v-model="otherEntity.partner_tax_id"
          :label="`Partner Tax ID`"
          property="partner_tax_id"
          :v$="v$"
          class="mb-5"
        />
      </template>
      <div class="mb-5">
        <VTaxIdField
          v-if="!is_non_us_investor"
          v-model="otherEntity.tax_id"
          label="Tax ID"
          format="ein"
          property="tax_id"
          :v$="v$"
          :description="`${t('shared.SSN / Tax ID')} is an encrypted attribute`"
        />
        <VTextField v-else v-model="otherEntity.tax_id" label="Tax ID" property="tax_id" :v$="v$" :disabled="true" />
        <p v-if="is_non_us_investor" class="mt-1 text-sm text-gray-500">
          {{ t('shared.SSN / Tax ID') }} is an encrypted attribute
        </p>
        <div class="mb-2 mt-2 flex items-center gap-3">
          <input type="checkbox" v-model="is_non_us_investor" />
          <p class="cursor-pointer text-sm font-medium text-gray-700" @click="toggleIsNonUsInvestor">
            Non-US investors, please check this box
          </p>
        </div>
        <VLink
          to="https://sa.www4.irs.gov/modiein/individual/legal-structure.jsp"
          class="mt-1 cursor-pointer text-sm font-medium text-[#51889B]"
          target="_blank"
          v-if="!is_non_us_investor"
        >
          Get Tax ID
        </VLink>
      </div>
      <VRadio
        v-model="otherEntity.input_capital"
        label="How do you like to allocate ownership in this entity?"
        :options="[
          { label: 'Capital', value: true },
          { label: 'Percent', value: false },
        ]"
        :v$="v$"
        optionsDirection="vertical"
        info="Depending on the entity type, you can allocate ownership in that entity based on either the capital contributed or by ownership percentage. Allocating ownership won't apply in all cases."
      />
    </div>
  </VSection>
  <VSection class="flex gap-3" id="administration">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
        {{ capitalize(t('shared.administration')) }}
        <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">2/5</span>
      </h3>
    </div>
    <div class="w-1/2">
      <div class="mb-5">
        <label for="" class="mb-1 block text-sm font-medium text-gray-700">
          {{ capitalize(t('shared.admin', 0)) }}
        </label>
        <VDropdownMultiple
          aligned="left"
          class="z-auto"
          :options="investorOptions"
          property="investor_admin_ids"
          v-model="otherEntity.investor_admin_ids"
          :show-selected-list="true"
        >
          <VButton size="xl" class="w-full">
            <div class="flex items-center justify-between">
              <div>Select multiple...</div>
              <VIcon name="ChevronSelectorVertical" />
            </div>
          </VButton>
        </VDropdownMultiple>
      </div>
    </div>
  </VSection>
  <VSection class="flex gap-3" id="structure">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
        {{ capitalize(t('shared.structure')) }}
        <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">3/5</span>
      </h3>
    </div>
    <div class="w-1/2">
      <VRadio
        v-model="useManagementFee"
        label="Management Fees"
        description="Do you track fees and display them to investors?"
        :options="[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]"
        default-option="Yes"
        options-direction="vertical"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.management_fee_percentage"
        v-if="useManagementFee"
        label="Annual Management Fee (%)"
        property="management_fee_percentage"
        description="Additional fees can be applied during capital calls."
        :v$="v$"
        class="mb-5"
      />
      <VRadio
        v-model="useCarriedInterest"
        label="Carried Interest"
        description="Do you participate in carried interest?"
        :options="[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]"
        default-option="Yes"
        options-direction="vertical"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.carried_interest"
        v-if="useCarriedInterest"
        label="Carried Interest (%)"
        property="carried_interest"
        :v$="v$"
        class="mb-5"
      />
    </div>
  </VSection>
  <VSection class="flex gap-3">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
        {{ capitalize(t('shared.address')) }}
        <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">4/5</span>
      </h3>
    </div>
    <div class="w-1/2">
      <VSelect
        v-model="otherEntity.country"
        :disabled="false"
        :label="capitalize(t('shared.country'))"
        :options="addressStore.countryOptions"
        property="country"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.street1"
        :label="capitalize(t('shared.street'))"
        property="street1"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.street2"
        :label="`Street (continued)`"
        property="street2"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.city"
        :label="capitalize(t('shared.city'))"
        property="city"
        :v$="v$"
        class="mb-5"
      />
      <VSelect
        v-model="otherEntity.state"
        :disabled="false"
        :label="startCase(t('shared.state / province'))"
        :options="addressStore.stateOptions"
        property="state"
        :v$="v$"
        class="mb-5"
      />
      <VTextField
        v-model="otherEntity.zip"
        :label="startCase(t('shared.zip / postal code'))"
        property="zip"
        :v$="v$"
        class="mb-5"
      />
    </div>
  </VSection>
  <VSection class="flex gap-3" id="disbursement-preference">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
        {{ capitalize(t('shared.disbursement preference')) }}
        <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">5/5</span>
      </h3>
    </div>
    <div class="w-1/2">
      <DisbursementPreferencesForm
        v-model="otherEntity"
        :v$="v$"
        :check_bank_account_number="!props.other_entity ? true : check_bank_account_number"
      />
      <div class="mt-24">
        <VButton variant="v-blue" size="lg" :click="saveOtherEntity">Confirm Changes</VButton>
      </div>
    </div>
  </VSection>
  <div class="my-16 h-1 bg-gray-50"></div>
  <VSection class="flex gap-3" v-if="props.other_entity">
    <div class="w-1/2">
      <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">Danger Zone</h3>
      <p class="mt-6">Warning, actions in this section are irreversible. Please proceed with caution.</p>
    </div>
    <div class="w-1/2">
      <VButton variant="secondary" size="lg" :click="removeOtherEntity"> Remove </VButton>
    </div>
  </VSection>
</template>
